import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Briefcase, Clock, PhoneCall, MapPin, TrendingUp, CircleDollarSign, AlertCircle, Star, CheckCircle2, ChevronRight, ChevronLeft } from "lucide-react";
import { dashboardApi } from "../../lib/api";
import { useNavigationHistory } from "../../contexts/NavigationHistoryContext";

const activityIconMap = {
  check: CheckCircle2, finished: CheckCircle2, dispatched: PhoneCall, arrived: MapPin,
  started: TrendingUp, invoiced: CircleDollarSign, note: AlertCircle, photo: Star,
  created: Briefcase, scheduled: Clock, enroute: MapPin, status: AlertCircle,
};
const activityColorMap = {
  finished: "#10B981", dispatched: "#F59E0B", arrived: "#10B981", started: "#2563EB",
  invoiced: "#2563EB", note: "#0EA5E9", photo: "#EC4899", created: "#64748B",
  scheduled: "#2563EB", enroute: "#F97316", status: "#475569",
};

const PAGE_SIZE = 15;

// All the distinct human-facing "activity type" buckets a row can fall into —
// used for the Activity Type filter. Order matches what was asked for, with
// the remaining real-world types appended after.
const ACTIVITY_TYPE_OPTIONS = [
  "All", "On Site", "Completed", "Job Created", "Follow Up", "Start Work",
  "Arrived", "En Route", "New Note", "Dispatched", "Scheduled",
  "Photo Uploaded", "Crew Change", "SMS", "Invoiced", "Status Update", "Other",
];

// Groups a raw activity row into one of ACTIVITY_TYPE_OPTIONS — labels/log
// entries are fairly free-text (e.g. GPS pings, crew changes, SMS failures),
// so this classifies by `type` first, falling back to the label text.
function classifyActivityType(a, title) {
  const type = a.type;
  const t = title.toLowerCase();
  if (t.startsWith("follow-up") || t.startsWith("follow up")) return "Follow Up";
  if (type === "created") return "Job Created";
  if (type === "finished") return "Completed";
  if (type === "arrived") return "Arrived";
  if (type === "enroute") return "En Route";
  if (type === "started") return "Start Work";
  if (type === "dispatched") return "Dispatched";
  if (type === "scheduled") return "Scheduled";
  if (type === "note") return "New Note";
  if (type === "photo") return "Photo Uploaded";
  if (type === "invoiced") return "Invoiced";
  if (type === "sms") return "SMS";
  if (type === "assigned") return "Crew Change";
  if (type === "location" || type === "status") {
    if (t.startsWith("on site") || t.includes("in progress")) return "On Site";
    if (t.startsWith("complete")) return "Completed";
    if (t.startsWith("start work")) return "Start Work";
    if (t.startsWith("en route")) return "En Route";
    if (t.includes("dispatch")) return "Dispatched";
    if (t.includes("scheduled")) return "Scheduled";
    return "Status Update";
  }
  if (t.includes("crew") || t.startsWith("assigned") || t.startsWith("unassigned")) return "Crew Change";
  return "Other";
}

export default function MobileAllActivity() {
  const { goBack } = useNavigationHistory();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [activityType, setActivityType] = useState("All");
  const [page, setPage] = useState(1);

  useEffect(() => {
    let active = true;
    dashboardApi.allActivity(500).then((rows) => { if (active) setActivity(rows || []); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const rows = useMemo(() => {
    return (activity || []).map((a) => {
      const rawLabel = (a.label || a.message || a.text || "").replace(/^[^\w]*/, "").trim();
      const title = (rawLabel.split(" at ")[0] || rawLabel || "Activity").trim();
      return { ...a, title, activityTypeLabel: classifyActivityType(a, title) };
    });
  }, [activity]);

  const filtered = useMemo(() => {
    const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
    return rows
      .filter((a) => {
        if (activityType !== "All" && a.activityTypeLabel !== activityType) return false;
        if (!fromMs && !toMs) return true;
        const t = a.time ? new Date(a.time).getTime() : 0;
        if (!t) return false;
        if (fromMs && t < fromMs) return false;
        if (toMs && t > toMs) return false;
        return true;
      })
      .sort((a, b) => (b.time || "").localeCompare(a.time || ""));
  }, [rows, from, to, activityType]);

  useEffect(() => { setPage(1); }, [from, to, activityType]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="mv relative w-full min-h-screen bg-slate-50 overflow-x-hidden flex flex-col" data-testid="mobile-all-activity-page">
      <header className="sticky top-0 z-40 flex items-center gap-2 px-3 h-14 bg-white border-b border-slate-200">
        <button
          onClick={() => goBack("/dashboard")}
          data-testid="mobile-all-activity-back"
          className="h-10 w-10 flex items-center justify-center rounded-full bg-slate-100 active:bg-slate-200 text-slate-700 shrink-0"
          aria-label="Back to dashboard"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
        </button>
        <div className="text-[16px] font-black tracking-tight text-[#0B1E3F]">All Activity</div>
      </header>

      <div className="mx-4 mt-4 bg-white rounded-2xl p-3 border border-slate-200" data-testid="all-activity-date-filter">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={to || undefined}
              data-testid="all-activity-from"
              className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          <div className="flex-1">
            <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              min={from || undefined}
              data-testid="all-activity-to"
              className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          {(from || to) && (
            <button
              onClick={() => { setFrom(""); setTo(""); }}
              data-testid="all-activity-clear-dates"
              className="self-end h-9 px-3 rounded-md text-[11.5px] font-semibold text-blue-600 hover:bg-blue-50"
            >
              Clear
            </button>
          )}
        </div>
        <div className="mt-3">
          <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">Activity Type</label>
          <select
            value={activityType}
            onChange={(e) => setActivityType(e.target.value)}
            data-testid="all-activity-type-filter"
            className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            {ACTIVITY_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mx-4 mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-14 text-center text-[13px] text-slate-400">Loading activity…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 text-center py-14" data-testid="mobile-all-activity-empty">
            <Clock className="w-12 h-12 text-slate-300 mb-3" strokeWidth={1.75} />
            <div className="text-base font-bold text-slate-700">No activity</div>
            <div className="text-sm text-slate-500 mt-1 max-w-[260px]">
              {(from || to || activityType !== "All") ? "No activity matches these filters." : "Activity will show up here."}
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="mobile-all-activity-list">
            {pageRows.map((a, idx) => {
              const Icon = activityIconMap[a.type] || activityIconMap[a.icon] || activityIconMap[a.kind] || Briefcase;
              const color = activityColorMap[a.type] || activityColorMap[a.icon] || activityColorMap[a.kind] || "#475569";
              const subtitle = [a.customer, a.time].filter(Boolean).join(" · ");
              return (
                <li key={a.id || idx} className="py-3 px-4 flex items-center gap-3" data-testid={`mobile-all-activity-row-${idx}`}>
                  <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}18` }}><Icon className="h-4 w-4" color={color} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-bold truncate" style={{ color: "#1E293B" }}>{a.title}</div>
                    <div className="text-[11.5px] mt-0.5 truncate" style={{ color: "#64748B" }}>{subtitle}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="mx-4 mt-3 flex items-center justify-between gap-2" data-testid="all-activity-pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            data-testid="all-activity-prev-page"
            className="h-9 px-3 rounded-md bg-white border border-slate-200 text-[12.5px] font-semibold text-slate-700 flex items-center gap-1 disabled:opacity-40"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </button>
          <div className="text-[12.5px] font-semibold text-slate-500" data-testid="all-activity-page-indicator">
            Page {currentPage} of {totalPages}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            data-testid="all-activity-next-page"
            className="h-9 px-3 rounded-md bg-white border border-slate-200 text-[12.5px] font-semibold text-slate-700 flex items-center gap-1 disabled:opacity-40"
          >
            Next <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Dedicated height-based spacer (not padding) so the list clears the fixed
          mobile nav — same pattern used on MobileDocDetail.jsx / MobileJobDetail.jsx. */}
      <div aria-hidden="true" className="md:hidden shrink-0" style={{ height: "calc(5rem + env(safe-area-inset-bottom))" }} />
    </div>
  );
}
